import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart3, Plus, Trash2, Edit2, Save, X, Star, DollarSign,
  Package, ChevronDown, ChevronUp, GripVertical, Weight, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrackingConsole() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    unit_label_singular: '',
    unit_label_plural: '',
    description: '',
    is_compensated: false,
    compensation_rate: '',
    compensation_type: 'per_unit',
    show_on_personnel: false,
    is_primary: false,
    task_types: []
  });

  const fetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/tracked-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data);
    } catch (err) {
      toast.error('Failed to load tracked items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setForm({
      name: '', unit_label_singular: '', unit_label_plural: '', description: '',
      is_compensated: false, compensation_rate: '', compensation_type: 'per_unit',
      show_on_personnel: false, is_primary: false, task_types: []
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name,
      unit_label_singular: item.unit_label_singular,
      unit_label_plural: item.unit_label_plural,
      description: item.description || '',
      is_compensated: item.is_compensated || false,
      compensation_rate: item.compensation_rate || '',
      compensation_type: item.compensation_type || 'per_unit',
      show_on_personnel: item.show_on_personnel || false,
      is_primary: item.is_primary || false,
      task_types: item.task_types || []
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.unit_label_singular || !form.unit_label_plural) {
      toast.error('Name and unit labels are required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...form,
        compensation_rate: form.compensation_rate ? parseFloat(form.compensation_rate) : null,
        task_types: form.task_types.map(tt => ({
          name: tt.name,
          weight: parseFloat(tt.weight) || 1.0,
          is_excluded: tt.is_excluded || false,
          compensation_override: tt.compensation_override ? parseFloat(tt.compensation_override) : null
        }))
      };

      if (editingId) {
        await axios.put(`${API}/tracked-items/${editingId}`, payload, { headers });
        toast.success('Tracked item updated');
      } else {
        await axios.post(`${API}/tracked-items`, payload, { headers });
        toast.success('Tracked item created');
      }
      resetForm();
      fetchItems();
    } catch (err) {
      toast.error('Failed to save tracked item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tracked item? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tracked-items/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tracked item deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete tracked item');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/tracked-items/seed-defaults`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchItems();
    } catch (err) {
      toast.error('Failed to seed defaults');
    }
  };

  const addTaskType = () => {
    setForm(prev => ({
      ...prev,
      task_types: [...prev.task_types, { name: '', weight: 1.0, is_excluded: false, compensation_override: null }]
    }));
  };

  const updateTaskType = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      task_types: prev.task_types.map((tt, i) => i === idx ? { ...tt, [field]: value } : tt)
    }));
  };

  const removeTaskType = (idx) => {
    setForm(prev => ({
      ...prev,
      task_types: prev.task_types.filter((_, i) => i !== idx)
    }));
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="tracking-console-title">
            <BarChart3 className="text-primary" size={24} />
            Tracking Console
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure what your organization tracks, how items are weighted, and compensation settings.
          </p>
        </div>
        <div className="flex gap-2">
          {items.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults} data-testid="seed-defaults-btn">
              <Package size={14} className="mr-2" />
              Load Healthcare Defaults
            </Button>
          )}
          <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-tracked-item-btn">
            <Plus size={14} className="mr-2" />
            New Tracked Item
          </Button>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <Card className="p-6 border-primary/30" data-testid="tracked-item-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              {editingId ? 'Edit Tracked Item' : 'New Tracked Item'}
            </h2>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X size={16} />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Item Name *</Label>
              <Input
                data-testid="tracked-item-name"
                placeholder="e.g., Patient Visits, Product Sales"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                data-testid="tracked-item-description"
                placeholder="Brief description of what's being tracked"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Unit Label (Singular) *</Label>
              <Input
                data-testid="tracked-item-singular"
                placeholder="e.g., visit, sale, policy"
                value={form.unit_label_singular}
                onChange={e => setForm({ ...form, unit_label_singular: e.target.value })}
              />
            </div>
            <div>
              <Label>Unit Label (Plural) *</Label>
              <Input
                data-testid="tracked-item-plural"
                placeholder="e.g., visits, sales, policies"
                value={form.unit_label_plural}
                onChange={e => setForm({ ...form, unit_label_plural: e.target.value })}
              />
            </div>
          </div>

          {/* Toggles Row */}
          <div className="flex flex-wrap items-center gap-6 mb-4 p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Switch
                data-testid="tracked-item-primary"
                checked={form.is_primary}
                onCheckedChange={v => setForm({ ...form, is_primary: v })}
              />
              <Label className="flex items-center gap-1 cursor-pointer">
                <Star size={14} className="text-amber-500" /> Primary Item
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="tracked-item-compensated"
                checked={form.is_compensated}
                onCheckedChange={v => setForm({ ...form, is_compensated: v })}
              />
              <Label className="flex items-center gap-1 cursor-pointer">
                <DollarSign size={14} className="text-green-600" /> Tied to Compensation
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="tracked-item-personnel"
                checked={form.show_on_personnel}
                onCheckedChange={v => setForm({ ...form, show_on_personnel: v })}
              />
              <Label className="flex items-center gap-1 cursor-pointer">
                <Eye size={14} /> Show on Personnel Page
              </Label>
            </div>
          </div>

          {/* Compensation Settings */}
          {form.is_compensated && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
              <div>
                <Label>Compensation Rate ($)</Label>
                <Input
                  data-testid="tracked-item-comp-rate"
                  type="number"
                  step="0.01"
                  placeholder="e.g., 50.00"
                  value={form.compensation_rate}
                  onChange={e => setForm({ ...form, compensation_rate: e.target.value })}
                />
              </div>
              <div>
                <Label>Compensation Type</Label>
                <Select
                  value={form.compensation_type}
                  onValueChange={v => setForm({ ...form, compensation_type: v })}
                >
                  <SelectTrigger data-testid="tracked-item-comp-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_unit">Per Unit</SelectItem>
                    <SelectItem value="flat_weekly">Flat Weekly</SelectItem>
                    <SelectItem value="tiered">Tiered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Task Types / Sub-Items */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold flex items-center gap-1">
                <Weight size={14} /> Task Types & Weights
              </Label>
              <Button variant="outline" size="sm" onClick={addTaskType} data-testid="add-task-type-btn">
                <Plus size={14} className="mr-1" /> Add Type
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Define sub-types with individual weights. Excluded types won't count in reports.
            </p>

            {form.task_types.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground text-sm border border-dashed rounded-lg">
                No task types defined. Click "Add Type" to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {/* Header */}
                <div className="grid grid-cols-12 gap-2 px-2 text-xs font-medium text-muted-foreground">
                  <div className="col-span-4">Name</div>
                  <div className="col-span-2">Weight</div>
                  <div className="col-span-2">Excluded</div>
                  <div className="col-span-3">{form.is_compensated ? 'Comp Override ($)' : ''}</div>
                  <div className="col-span-1"></div>
                </div>
                {form.task_types.map((tt, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 bg-muted/20 rounded" data-testid={`task-type-row-${idx}`}>
                    <div className="col-span-4">
                      <Input
                        data-testid={`task-type-name-${idx}`}
                        placeholder="e.g., PT Visit"
                        value={tt.name}
                        onChange={e => updateTaskType(idx, 'name', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        data-testid={`task-type-weight-${idx}`}
                        type="number"
                        step="0.5"
                        min="0"
                        value={tt.weight}
                        onChange={e => updateTaskType(idx, 'weight', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="col-span-2 flex items-center">
                      <Switch
                        checked={tt.is_excluded}
                        onCheckedChange={v => updateTaskType(idx, 'is_excluded', v)}
                      />
                      <span className="ml-1 text-xs text-muted-foreground">{tt.is_excluded ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="col-span-3">
                      {form.is_compensated && (
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Override"
                          value={tt.compensation_override || ''}
                          onChange={e => updateTaskType(idx, 'compensation_override', e.target.value || null)}
                          className="h-8 text-sm"
                        />
                      )}
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button variant="ghost" size="sm" onClick={() => removeTaskType(idx)} className="h-8 w-8 p-0 text-destructive">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-tracked-item-btn">
              <Save size={14} className="mr-2" />
              {editingId ? 'Update' : 'Create'}
            </Button>
          </div>
        </Card>
      )}

      {/* Items List */}
      {items.length === 0 && !showForm ? (
        <Card className="p-12 text-center" data-testid="tracking-empty-state">
          <BarChart3 size={48} className="mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold mb-1">No Tracked Items Yet</h3>
          <p className="text-muted-foreground mb-4">
            Set up what your organization tracks. This determines how reports are generated and labeled.
          </p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={handleSeedDefaults} data-testid="empty-seed-btn">
              <Package size={14} className="mr-2" />
              Load Healthcare Defaults
            </Button>
            <Button onClick={() => setShowForm(true)}>
              <Plus size={14} className="mr-2" />
              Create Custom
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="tracked-items-list">
          {items.map(item => (
            <Card key={item.id} className={`overflow-hidden transition-all ${item.is_primary ? 'border-primary/50 shadow-sm' : ''}`} data-testid={`tracked-item-${item.id}`}>
              {/* Item Header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/20 transition-colors"
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.is_primary ? 'bg-primary/10 text-primary' : 'bg-muted'}`}>
                    <BarChart3 size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{item.name}</h3>
                      {item.is_primary && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Star size={10} /> Primary
                        </span>
                      )}
                      {item.is_compensated && (
                        <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <DollarSign size={10} /> Compensated
                        </span>
                      )}
                      {item.status === 'inactive' && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Inactive</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Unit: {item.unit_label_singular} / {item.unit_label_plural}
                      {item.task_types?.length > 0 && ` | ${item.task_types.length} task type${item.task_types.length > 1 ? 's' : ''}`}
                      {item.is_compensated && item.compensation_rate && ` | $${item.compensation_rate}/${item.unit_label_singular}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(item); }} data-testid={`edit-item-${item.id}`}>
                    <Edit2 size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="text-destructive" data-testid={`delete-item-${item.id}`}>
                    <Trash2 size={14} />
                  </Button>
                  {expandedId === item.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === item.id && (
                <div className="px-4 pb-4 border-t">
                  {item.description && (
                    <p className="text-sm text-muted-foreground mt-3 mb-3">{item.description}</p>
                  )}

                  {/* Settings Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-xs text-muted-foreground">Show on Personnel</p>
                      <p className="font-medium text-sm">{item.show_on_personnel ? 'Yes' : 'No'}</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-xs text-muted-foreground">Compensation Type</p>
                      <p className="font-medium text-sm capitalize">{item.is_compensated ? (item.compensation_type || 'per_unit').replace('_', ' ') : 'N/A'}</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-xs text-muted-foreground">Task Types</p>
                      <p className="font-medium text-sm">{item.task_types?.length || 0}</p>
                    </div>
                    <div className="p-2 bg-muted/30 rounded text-center">
                      <p className="text-xs text-muted-foreground">Status</p>
                      <p className={`font-medium text-sm ${item.status === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                        {item.status || 'active'}
                      </p>
                    </div>
                  </div>

                  {/* Task Types Table */}
                  {item.task_types && item.task_types.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm mb-2">Task Types & Weights</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left p-2">Type Name</th>
                              <th className="text-center p-2">Weight</th>
                              <th className="text-center p-2">Excluded</th>
                              {item.is_compensated && <th className="text-center p-2">Comp Override</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {item.task_types.map((tt, idx) => (
                              <tr key={idx} className="border-b border-muted/30">
                                <td className="p-2">{tt.name}</td>
                                <td className="text-center p-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${tt.weight > 1 ? 'bg-blue-100 text-blue-700' : tt.weight === 0 ? 'bg-red-100 text-red-700' : 'bg-muted'}`}>
                                    {tt.weight}x
                                  </span>
                                </td>
                                <td className="text-center p-2">
                                  {tt.is_excluded ? (
                                    <span className="text-red-500 text-xs">Excluded</span>
                                  ) : (
                                    <span className="text-green-600 text-xs">Counted</span>
                                  )}
                                </td>
                                {item.is_compensated && (
                                  <td className="text-center p-2">
                                    {tt.compensation_override ? `$${tt.compensation_override}` : '-'}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
